import { Loader } from '@10xLabs/react-web/components/Loader'
import { Map, useMap } from '@vis.gl/react-google-maps'
import { flatten, isEmpty } from 'lodash'
import { ReactElement, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { Polyline } from 'components/GoogleMaps/Polyline'
import EmptyState from 'components/v2/EmptyState'
import { googleMapsConfig } from 'config/google-maps'
import { ClusteredMarkers } from 'features/RoutesPlanning/components/RoutesPlanningMap/ClusteredMarkers'
import { TStopsGeojson } from 'features/RoutesPlanning/types'
import { mapRoutesStopsToGeojson } from 'features/RoutesPlanning/utils'
import { IRoutePlanningModel } from 'features/RoutesPlanning/models'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { adjustMapBounds } from 'utils/google-maps'
import { DEFAULT_MAP_STYLE_ID } from 'config/constants'

const Container = styled.div`
  position: relative;
  height: 100%;
  min-height: 300px;
`

interface IRoutesPlanningMapProps {
  routes: IRoutePlanningModel[]
}

export const RoutesPlanningMap = ({ routes }: IRoutesPlanningMapProps): ReactElement => {
  const mapInstance = useMap()
  const geojson: TStopsGeojson = useMemo(() => {
    return mapRoutesStopsToGeojson(routes)
  }, [routes])

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsScript()
  const { defaultCenter, defaultZoom } = googleMapsConfig

  const decodedRoutesPolylines = useMemo(() => {
    if (isGoogleMapsLoaded) {
      return routes.map((route) => {
        return route.polyline.decodedPath
      })
    }
    return []
  }, [routes, isGoogleMapsLoaded])

  useEffect(() => {
    if (!mapInstance || isEmpty(decodedRoutesPolylines)) return

    adjustMapBounds(flatten(decodedRoutesPolylines), mapInstance)
  }, [mapInstance, decodedRoutesPolylines])

  if (!isGoogleMapsLoaded) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  return (
    <Container>
      <Map
        mapId={DEFAULT_MAP_STYLE_ID}
        defaultZoom={defaultZoom}
        defaultCenter={{
          lat: defaultCenter.LAT,
          lng: defaultCenter.LNG
        }}
        {...googleMapsConfig.defaultOptions}
      >
        {decodedRoutesPolylines.map((decodedPolyline, index) => {
          if (isEmpty(decodedPolyline)) return null

          const color = routes[index].rgbColor

          return (
            <Polyline
              key={index}
              path={decodedPolyline}
              options={{
                geodesic: true,
                strokeColor: color,
                strokeOpacity: 1,
                strokeWeight: 3
              }}
            />
          )
        })}
        {geojson && <ClusteredMarkers geojson={geojson} />}
      </Map>
    </Container>
  )
}
